import { Location } from 'react-router-dom';

import {
  JourneysData,
  JourneyOptions,
  JourneyName,
  JourneyData,
  StepName,
  StepData,
  ActionName,
} from './types';

import { JourneysState } from './journeysSlice';
import journeysConfig from './journeys.json';

export const journeyPathPrefixes = [
  ...new Set(
    Object.values(journeysConfig.journeys).flatMap(
      journey => Object.entries(journey)
        // Starts with basePath as we also have basePathAdopt
        .filter(([key, value]) => key.startsWith('basePath') && typeof value === 'string')
        .map(([, value]) => value as string)
    )
  ),
];

export const buildJourneyOptions = (journeysData: JourneysData, journeyKeys: string[]): JourneyOptions => (
  Object.fromEntries(
    Object.entries(journeysData.journeys)
      .filter(
        ([journeyName]) => journeyKeys.includes(journeyName)
      )
      .map(
        ([journeyName, { name, basePath, basePathAdopt }]) => (
          [
            journeyName,
            {
              name,
              basePath,
              basePathAdopt
            }
          ]
        )
      )
  )
);

export const loadJourney = (journeysData: JourneysData, journeyName: JourneyName): JourneyData => {
  if (!(journeyName in journeysData.journeys)) {
    throw new Error(`Journey ${journeyName} does not exist`);
  }

  return journeysData.journeys[journeyName];
};

export const getJourneyStep = (journey: JourneyData, stepName: StepName): StepData => {
  if (!(stepName in journey.steps)) {
    throw new Error(`Step ${stepName} does not exist`);
  }

  return journey.steps[stepName];
};

export const getStepAction = (step: StepData, action: ActionName): StepName => {
  if (!(action in step.actions)) {
    throw new Error(`Action ${action} does not exist`);
  }

  return step.actions[action];
};

export const checkConditionalRules = (
  journey: JourneyData,
  currentStep: StepName,
  action: ActionName,
  isAARN: boolean,
  hasPostalRewards: boolean
): StepName | null => {
  if (!journey.conditionalRules) {
    return null;
  }

  const matchingRule = journey.conditionalRules.find((rule) => {
    const { condition } = rule;

    if (condition.step === currentStep && condition.action === action) {
      const requiresAARN = condition.requiresAARN ?? false;
      const requiresPostalRewards = condition.requiresPostalRewards ?? false;

      if (requiresAARN && !isAARN) return false;
      if (requiresPostalRewards && !hasPostalRewards) return false;

      return true;
    }
    return false;
  });

  return matchingRule ? matchingRule.targetStep : null;
};

export const getCurrentJourney = (journeysState: JourneysState): JourneyName => {
  const { journey } = journeysState;
  if (!journey) {
    throw new Error('Journey not set');
  }

  return journey;
};

export const determineCurrentStep = (
  journey: JourneyData,
  location: Location,
  isAARN = false,
  overrideCurrentStep?: string
): StepName => {
  let path;

  if (overrideCurrentStep) {
    path = overrideCurrentStep;
  } else {
    const { basePath, basePathAdopt } = journey;

    // The isAARN flag doesn't always come through as true in time, so we need to check the URL path to check if we should use the AARN path
    const shouldUseAARN = isAARN || (basePathAdopt && location.pathname.startsWith(basePathAdopt));
    const thisBasePath = shouldUseAARN && basePathAdopt ? basePathAdopt : basePath;
    const fullPath = location.pathname;
    path = fullPath.replace(thisBasePath, '');
  }

  if (!(path in journey.steps)) {
    throw new Error(`Path "${path}" does not exist in journey`);
  }

  return path;
};

// Helper function to always show a decimal amount to 2 places:
export const formatAmount = (thisAmount:number) => {
  const hasDecimals = Boolean(thisAmount.toString().split('.')[1]);

  return hasDecimals
    ? (Math.round(thisAmount * 100) / 100).toFixed(2)
    : thisAmount;
};
